import React, {useContext, useEffect, useLayoutEffect, useState} from "react";
import {Platform, ScrollView} from "react-native";
import ServiceClient from "../utils/ServiceClient";
import {CancelModalProps} from "../types";
import {StoreContext} from "../components/StoreContextProvider";
import useIdleLogout from "../hooks/useIdleLogout";
import {
  ListTournamentRequest_Sort, ListTournamentRequest_TournamentAccess,
  Tournament, TournamentPlayerBet_State, TournamentState, User,
} from "../../generated/smg_service_pb";
import {useIsFocused, useNavigation} from "@react-navigation/native";
import NewHeader from "../components/NewHeader";
import MainStyle from "../../MainStyle";
import Div from "../lib/Div";
import {colors} from "../assets/styles/colors";
import BottomNavigation from "../components/BottomNavigation";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment";
import {isFullSwing, LANDING_PAGE_URL, SESSION_EXPIRE_TIME} from "../constants";
import P from "../lib/P";
import Button from "../components/Button";
import {formatPayout} from "../utils";
import FSHeader from "../components/FSHeader";
import FontAwesome from "@expo/vector-icons/FontAwesome";
import Btn from "../components/Btn";

const Sort = ListTournamentRequest_Sort;
const TournamentAccess = ListTournamentRequest_TournamentAccess;

const CompletedFriendChallenges = () => {
  const Context = useContext(StoreContext);
  const {cancelModalConfig, user, session} = Context;
  const [tournamentsList, setTournamentsList] = useState<Tournament[]>([]);
  const navigation: any = useNavigation();
  const isFocused = useIsFocused();

  const getPlayerDetails = (data: Tournament) => {
    const {playerDetails, currentPlayerDetails} = data;
    const currPlayer = playerDetails.find(el => el.playerId === currentPlayerDetails?.playerId);
    let winnerNames = '';
    if (!currPlayer) return {payout: 0};

    playerDetails.forEach(player => {
      if (player.won) {
        if (!winnerNames) winnerNames = player.playerName;
        else winnerNames += `, ${player.playerName}`;
      }
    });

    return {
      players: playerDetails.length, position: currPlayer.rank, winnerNames, payout: currPlayer.prize,
      didIWin: currPlayer.won
    };
  };

  const getCompletedPrivateTournaments = () => {
    const states = [TournamentState.FINISHED, TournamentState.CANCELLED, TournamentState.PLAYING];

    ServiceClient.listTournaments(states, Sort.FINISH_TIME, [TournamentAccess.FRIENDS], true).then(res => {
      Context.setLoadingOverlay(false);

      const tournaments = res.filter(el => {
        const currentNumberOfShots = el?.currentPlayerDetails?.bets.filter(bet => bet.state === TournamentPlayerBet_State.FINISHED && bet.resolved).length || 0;
        return el.state === TournamentState.PLAYING ? currentNumberOfShots === el.totalShots : true;

      }).sort((a, b) => {
        if (a.entryEndDate > b.entryEndDate) return -1;
        if (a.entryEndDate < b.entryEndDate) return 1;
        return 0;
      });

      setTournamentsList(tournaments);

    }).catch(err => {
      console.log('listTournaments err', err);
      Context.setLoadingOverlay(false);

      let conf: CancelModalProps = {
        visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          if (err.code !== 20020042) navigation.navigate({name: 'account', params: {step: 1}});
        }
      };

      Context.setCancelModalConfig(conf);
    });
  };

  const getUser = () => {
    Context.getUser().catch(err => {
      console.log('getUser err', err);
      setTimeout(() => {
        getUser();
      }, 5000);
    });
  };

  useEffect(() => {
    if (!user || !isFocused) return;

    getCompletedPrivateTournaments();

    const time = setTimeout(() => {
      getUser();
    }, 1000 * 10);

    return () => {
      clearTimeout(time);
    };
  }, [user, isFocused]);

  useIdleLogout({enabled: isFocused, onLogout: () => Context.setLoadingOverlay(false)});

  useLayoutEffect(() => {
    Context.setLoadingOverlay(true);

    if (!session || !user) {
      (async () => {
        const time = await AsyncStorage.getItem('lastActiveTime');

        if (time && moment().diff(moment(time), 'minutes') > SESSION_EXPIRE_TIME) {
          Context.logout().then(() => {
            if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
            Context.setLoadingOverlay(false);
          });

        } else {
          const val = await AsyncStorage.getItem('session');
          let curUser: User | undefined;

          if (val) {
            const Session = JSON.parse(val);
            Context.setSession(Session);

            try {
              curUser = await Context.getUser(Session);

            } catch (e: any) {
              Context.setCancelModalConfig({
                visible: true, title: 'Error', subtitle: e?.message || 'Error', rightBtnText: 'OK',
                onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
              });
            }
          }

          if (!val || !curUser || !curUser.userId) {
            Context.logout().then(() => {
              if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
              Context.setLoadingOverlay(false);
            });

          } else console.log('user', curUser);
        }
      })();
    }
  }, []);

  return (
    <>
      {isFullSwing ? (
        <>
          <FSHeader px={20}/>

          <Div h={2} bg="#252526"/>

          <Div gap={5} py={10} px={20}>
            <Div row alignItems="center">
              <Div w={24} alignItems="center"><FontAwesome name="user-circle" size={24} color="#3399FF"/></Div>
              <P fontFamily="DavisSans-Regular" ml={6}>{user?.username}</P>
            </Div>
          </Div>

          <Div h={2} bg="#252526"/>
        </>
      ) : <NewHeader/>}

      <ScrollView contentContainerStyle={MainStyle.flex1} scrollEventThrottle={16}>
        {!isFullSwing && <P fw={500} fs={32} ml={21}>Play With Friends</P>}

        <Div row justifyContent="space-between" mb={10} mt={isFullSwing ? 10 : 0} alignItems="stretch">
          <P fw={500} fs={22} ml={21}>{!isFullSwing && '/'} Friend Challenge Details</P>
        </Div>

        {!isFocused && <Div h={1} bg={colors.divider_bg} mx={23} mb={18}/>}

        {tournamentsList.map(el => {
          const {players, position, winnerNames, payout, didIWin} = getPlayerDetails(el);

          return (
            <Btn mx="auto" p={10} key={el.id} w={360} h={160} rounded={isFullSwing ? 2 : 20} borderWidth={1} mb={10}
                 borderColor={didIWin ? isFullSwing ? '#0AF' : colors.newGreenText : isFullSwing ? '#4C4C4C' : colors.settings_border}
                 bg={isFullSwing ? '#252526' : colors.settings_bg}
                 onPress={() => navigation.navigate('TournamentDetails', {
                   id: el.id,
                   isPrivateTournament: el.isPrivate
                 })}>
              <P color={colors.id_text} ta="center" fw={500} fs={20} mb={16}>
                {el.finishDate ? moment.unix(Number(el.finishDate)).format('LL') : 'Ongoing'}
              </P>

              <Div row>
                <P flex={1} ta="center" fw={500} fs={14}>PLAYERS</P>
                <P flex={1} ta="center" fw={500} fs={14}>ENTRY FEE</P>
                <P flex={1} ta="center" fw={500} fs={14}>SHOTS</P>
                <P flex={1} ta="center" fw={500} fs={14}>SCORE</P>
              </Div>

              <Div row mb={13}>
                <P flex={1} ta="center" fw={700}>{players}</P>
                <P flex={1} ta="center" fw={700}>{formatPayout(el.entryFee / 100)}</P>
                <P flex={1} ta="center" fw={700}>
                  {el.currentPlayerDetails?.bets.filter(bet => bet.state === TournamentPlayerBet_State.FINISHED && bet.resolved).length}
                </P>
                <P flex={1} ta="center" fw={700}>{el.currentPlayerDetails?.score}</P>
              </Div>

              <Div row justifyContent="space-around">
                <Div>
                  <P ta="center" fw={500} fs={14}>POSITION</P>
                  <P ta="center" fw={700} color={isFullSwing ? '#0AF' : colors.newGreenText}>{position}</P>
                </Div>

                <Div maxW="50%">
                  <P ta="center" fw={500} fs={14}>WINNER</P>
                  <P ta="center" fw={700} numberOfLines={1}
                     color={didIWin ? isFullSwing ? '#0AF' : colors.newGreenText : ''}>
                    {winnerNames || '-'}
                  </P>
                </Div>

                <Div>
                  <P ta="center" fw={500} fs={14}>PAYOUT</P>
                  <P ta="center" fw={700} color={isFullSwing ? '#0AF' : colors.newGreenText}>
                    {formatPayout(payout / 100)}
                  </P>
                </Div>
              </Div>
            </Btn>
          );
        })}
      </ScrollView>

      {!isFullSwing && <Div h={1} bg={colors.divider_bg} mx={23} mb={18}/>}

      <Button
        text="OK" w={350} mx="auto" fontWeight="600" mb={13} radius={isFullSwing ? 4 : undefined}
        bg={isFullSwing ? '#3369FF' : undefined} color={isFullSwing ? 'white' : undefined}
        onPress={() => navigation.reset({index: 0, routes: [{name: 'FriendsScreen'}]})}
      />

      <P ta="center" fw={500} mb={20} color={colors.gray}>Malfunction voids all pays and plays</P>

      {!isFullSwing && <BottomNavigation page="FriendsScreen"/>}
    </>
  );
};

export default CompletedFriendChallenges;
